import { type ChangeEvent, type ReactNode, useCallback, useMemo } from "react";
import { LuCircleCheck as LuCheckCircle, LuRefreshCcw } from "react-icons/lu";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type {
  FilterDateValue,
  FilterMatchStringValue,
  FilterNumericValue,
  FilterNumericValueWithTimePeriod,
  FilterRecord,
} from "@/contexts/filters-sort-context";
import { FilterType, TimePeriodLabels } from "@/contexts/filters-sort-context";
import { FocusDateParser } from "@/utils/date";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";

import { TimePeriod } from "../app/discover-sort-constants";
import type { Option } from "../ui/multiselect";
import MultipleSelector from "../ui/multiselect";
import UserMultipleSelect from "./users-search/user-multiple-select";

type FilterMethods<T> = {
  findFilterByName: (name: T) => FilterRecord | undefined;
  updateFilter: (filter: FilterRecord) => void;
  removeFilter: (name: T) => void;
  isFilterActive: (name: T) => boolean;
  isFilterDisabled?: (name: T) => boolean;
  isTimePeriodDisabled?: boolean;
};

export enum NotificationOrderBySortKey {
  orderBy_time = "orderBy_time",
  orderBy_value = "orderBy_value",
}

export const FiltersSet = ({
  name,
  children,
  isSectionDisabled,
}: {
  name: string;
  children: ReactNode;
  isSectionDisabled?: boolean;
}) => {
  if (isSectionDisabled) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div
        className="
          mb-0 border-b border-border-light pb-2 font-semibold text-muted
        "
      >
        {name}
      </div>
      {children}
    </div>
  );
};

export const FilterString = <T extends string>({
  label,
  name,
  icon,
  placeholder,
  defaultValue = "",
  findFilterByName,
  updateFilter,
  removeFilter,
  isFilterActive,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  placeholder?: string;
  icon: ReactNode;
  defaultValue?: string;
} & FilterMethods<T>) => {
  const value = findFilterByName(name)?.value || defaultValue;

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value === "") {
        removeFilter(name);
        return;
      }

      updateFilter({
        name,
        type: FilterType.string,
        value,
      });
    },
    [name, removeFilter, updateFilter],
  );

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <Input
            type="text"
            onChange={onChange}
            value={value as string}
            placeholder={placeholder}
            className={cn(
              `
                border border-border bg-accent
                placeholder:text-muted
              `,
            )}
            autoFocus={true}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterSingleNumber = <T extends string>({
  label,
  name,
  icon,
  placeholder,
  defaultValue = "",
  findFilterByName,
  updateFilter,
  removeFilter,
  isFilterActive,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  placeholder?: string;
  icon: ReactNode;
  defaultValue?: string;
} & FilterMethods<T>) => {
  const value = findFilterByName(name)?.value || defaultValue;

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value === "") {
        removeFilter(name);
        return;
      }

      updateFilter({
        name,
        type: FilterType.singleNumber,
        value: Number(value),
      });
    },
    [name, removeFilter, updateFilter],
  );

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <Input
            type="number"
            onChange={onChange}
            value={value as string}
            placeholder={placeholder}
            className={cn(
              `
                border border-border bg-accent
                placeholder:text-muted
              `,
            )}
            autoFocus={true}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterWrapper = <T extends string>({
  label,
  icon,
  name,
  isFilterActive,
}: {
  label: string;
  icon: ReactNode;
  name: T;
  isFilterActive: (name: T) => boolean;
}) => {
  const isActive = isFilterActive(name);

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-4 text-sm text-foreground">
        <span className="text-xl text-muted">{icon}</span>
        {label}
      </div>
      {isActive && (
        <div className="pr-2">
          <LuCheckCircle className="text-xl text-green-500" />
        </div>
      )}
    </div>
  );
};

export const FilterMatchString = <T extends string>({
  label,
  name,
  icon,
  placeholders,
  defaultValue = {
    any: "",
    all: "",
    none: "",
  },
  findFilterByName,
  updateFilter,
  isFilterDisabled,
  isFilterActive,
}: {
  label: string;
  name: T;
  placeholders?: string[];
  icon: ReactNode;
  defaultValue?: FilterMatchStringValue;
} & FilterMethods<T>) => {
  const [anyPlaceholder, allPlaceholder, nonePlaceholder] = placeholders || [
    "Contains any of...",
    "Contains all of...",
    "Contains none of...",
  ];

  const value =
    (findFilterByName(name)?.value as FilterMatchStringValue) || defaultValue;

  const onMatchInputChange =
    (key: "any" | "all" | "none") => (e: ChangeEvent<HTMLInputElement>) => {
      updateFilter({
        name,
        type: FilterType.matchString,
        value: {
          ...value,
          [key]: e.target.value,
        } as FilterMatchStringValue,
      });
    };

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <Input
            type="text"
            onChange={onMatchInputChange("any")}
            value={value.any}
            placeholder={anyPlaceholder}
            className={cn(
              `
                border border-border bg-accent
                placeholder:text-muted
              `,
            )}
            autoFocus={true}
          />
          <Input
            type="text"
            onChange={onMatchInputChange("all")}
            value={value.all}
            placeholder={allPlaceholder}
            className={cn(
              `
                border border-border bg-accent
                placeholder:text-muted
              `,
            )}
          />
          <Input
            type="text"
            onChange={onMatchInputChange("none")}
            value={value.none}
            placeholder={nonePlaceholder}
            className={cn(
              `
                border border-border bg-accent
                placeholder:text-muted
              `,
            )}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterDate = <T extends string>({
  label,
  icon,
  labels: [afterLabel, beforeLabel] = ["After", "Before"],
  name,
  defaultValue = {
    after: FocusDateParser.now(),
    before: FocusDateParser.add(1, "day"),
  },
  findFilterByName,
  updateFilter,
  removeFilter,
  isFilterDisabled,
  isFilterActive,
}: {
  icon: ReactNode;
  name: T;
  label: string;
  labels?: [string, string];
  defaultValue?: FilterDateValue;
} & FilterMethods<T>) => {
  const value =
    (findFilterByName(name)?.value as FilterDateValue) || defaultValue;

  const onDateInputChange = (key: "after" | "before") => (date?: Date) => {
    updateFilter({
      name,
      type: FilterType.date,
      value: {
        ...value,
        [key]: date,
      } as FilterDateValue,
    });
  };

  const onResetClick = useCallback(() => {
    removeFilter(name);
  }, [name, removeFilter]);

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mt-1 mb-4 overflow-visible rounded-lg border">
        <div className="flex flex-col">
          <div
            className="
              flex w-full flex-col items-start justify-between gap-2 border-b
              border-border-light p-4 py-2 pr-2 text-sm
              md:flex-row md:items-center md:gap-0
            "
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-muted-foreground">
                {afterLabel}
              </span>
            </div>
            <div>
              <DatePicker
                {...{
                  date: value.after,
                  setDate: onDateInputChange("after"),
                }}
              />
            </div>
          </div>
          <div
            className="
              flex w-full flex-col items-start justify-between gap-2 border-b
              border-border-light p-4 py-2 pr-2 text-sm
              md:flex-row md:items-center md:gap-0
            "
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-muted-foreground">
                {beforeLabel}
              </span>
            </div>
            <div>
              <DatePicker
                {...{
                  date: value.before,
                  setDate: onDateInputChange("before"),
                }}
              />
            </div>
          </div>
        </div>
        <div className="px-4">
          <Button
            size="sm"
            variant="link"
            onClick={onResetClick}
            className="mt-4 flex h-auto items-center gap-2 p-0 text-muted"
          >
            <LuRefreshCcw />
            Reset
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterNumericRangeWithTimePeriod = <T extends string>({
  label,
  name,
  icon,
  placeholders = ["More than", "Less than"],
  defaultValue = {
    timePeriod: TimePeriod.AllTime,
  },
  findFilterByName,
  updateFilter,
  removeFilter,
  isTimePeriodDisabled,
  isFilterActive,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  placeholders?: string[];
  icon: ReactNode;
  isTimePeriodDisabled?: boolean;
  defaultValue?: FilterNumericValueWithTimePeriod;
} & FilterMethods<T>) => {
  const [moreThenPlaceholder, lessThenPlaceholder] = placeholders;

  const value = useMemo(
    () =>
      (findFilterByName(name)?.value as FilterNumericValueWithTimePeriod) ||
      defaultValue,
    [defaultValue, findFilterByName, name],
  );

  const onRangeInputChange =
    (key: "min" | "max") => (e: ChangeEvent<HTMLInputElement>) => {
      updateFilter({
        name,
        type: FilterType.numericWithTime,
        value: {
          ...value,
          [key]: Number(e.target.value),
        } as FilterNumericValueWithTimePeriod,
      });
    };

  const onSelectRangeChange = useCallback(
    (timePeriod: TimePeriod) => {
      updateFilter({
        name,
        type: FilterType.numericWithTime,
        value: {
          ...value,
          timePeriod,
        } as FilterNumericValueWithTimePeriod,
      });
    },
    [name, updateFilter, value],
  );

  const onResetClick = useCallback(() => {
    removeFilter(name);
  }, [name, removeFilter]);

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <Select
            onValueChange={onSelectRangeChange}
            defaultValue={value.timePeriod}
            disabled={isTimePeriodDisabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a deposit currency" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TimePeriodLabels).map(([key, label]) => (
                <SelectItem value={key} key={key} className="cursor-pointer">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FilterNumericRange
            {...{
              min: value.min,
              max: value.max,
              moreThenPlaceholder,
              lessThenPlaceholder,
              onRangeInputChange,
            }}
          />
        </div>
        <Button
          size="sm"
          variant="link"
          onClick={onResetClick}
          className="mt-4 flex h-auto items-center gap-2 p-0 text-muted"
        >
          <LuRefreshCcw />
          Reset
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterSelect = <T extends string>({
  label,
  name,
  icon,
  defaultValue = "",
  findFilterByName,
  updateFilter,
  removeFilter,
  options,
  isFilterActive,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  icon: ReactNode;
  options: { value: string; label: string }[];
  defaultValue?: string;
} & FilterMethods<T>) => {
  const value = useMemo(
    () => (findFilterByName(name)?.value as string) || defaultValue,
    [defaultValue, findFilterByName, name],
  );

  const onSelectChange = useCallback(
    (value: string) => {
      updateFilter({
        name,
        type: FilterType.select,
        value,
      });
    },
    [name, updateFilter],
  );

  const onResetClick = useCallback(() => {
    removeFilter(name);
  }, [name, removeFilter]);

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <Select onValueChange={onSelectChange} value={value}>
            <SelectTrigger>
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              {options.map(({ value, label }) => (
                <SelectItem
                  value={value}
                  key={value}
                  className="cursor-pointer"
                >
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          variant="link"
          onClick={onResetClick}
          className="mt-4 flex h-auto items-center gap-2 p-0 text-muted"
        >
          <LuRefreshCcw />
          Reset
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterMultiSelect = <T extends string>({
  label,
  name,
  icon,
  defaultValue = [],
  findFilterByName,
  updateFilter,
  isFilterDisabled,
  removeFilter,
  options,
  isFilterActive,
}: {
  label: string;
  name: T;
  icon: ReactNode;
  options: Option[];
  defaultValue?: string[];
} & FilterMethods<T>) => {
  const multiSelectValueList: string[] = useMemo(() => {
    const filter = findFilterByName(name);

    if (filter?.type === FilterType.multiSelect) {
      return filter?.value;
    }

    return defaultValue;
  }, [defaultValue, findFilterByName, name]);

  const valueOptions = useMemo(
    () => options.filter(({ value }) => multiSelectValueList.includes(value)),
    [multiSelectValueList, options],
  );

  const onMultiSelectChange = useCallback(
    (valueList: Option[]) => {
      const newValue = valueList.map(({ value }) => value);

      if (newValue.length === 0) {
        removeFilter(name);
        return;
      }

      updateFilter({
        name,
        type: FilterType.multiSelect,
        value: valueList.map(({ value }) => value),
      });
    },
    [name, removeFilter, updateFilter],
  );

  const onResetClick = useCallback(() => {
    removeFilter(name);
  }, [name, removeFilter]);

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <MultipleSelector
            value={valueOptions}
            className="
              min-h-9 rounded-full border-border-light bg-input px-3 py-1.5
              text-sm text-muted-foreground focus-within:border-foreground
              focus-within:ring-0 focus-within:ring-offset-0
              hover:border-foreground
            "
            badgeClassName="flex"
            inputProps={{
              className: "min-h-6 text-sm placeholder:text-muted",
            }}
            placeholder="Select options"
            onChange={onMultiSelectChange}
            defaultOptions={options}
            selectFirstItem={false}
            emptyIndicator={
              <p className="text-center text-xs text-muted-foreground">
                No results found.
              </p>
            }
          />
        </div>
        <Button
          size="sm"
          variant="link"
          onClick={onResetClick}
          className="mt-4 flex h-auto items-center gap-2 p-0 text-muted"
        >
          <LuRefreshCcw />
          Reset
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
};

const FilterNumericRange = ({
  min,
  max,
  moreThenPlaceholder,
  lessThenPlaceholder,
  onRangeInputChange,
}: {
  min?: number | string;
  max?: number | string;
  moreThenPlaceholder: string;
  lessThenPlaceholder: string;
  onRangeInputChange: (
    key: "min" | "max",
  ) => (e: ChangeEvent<HTMLInputElement>) => void;
}) => {
  let error = "";

  const bothNumbersAreNotNaN =
    !isNaN(Number(min)) && !isNaN(Number(max)) && min !== "" && max !== "";

  if (Number(min) > Number(max) && bothNumbersAreNotNaN) {
    error = "Min value should be less than max value";
  }

  if ((Number(min) < 0 || Number(max) < 0) && bothNumbersAreNotNaN) {
    error = "Min and max values should be positive";
  }

  const className = `
    w-1/2 border border-border bg-accent
    placeholder:text-muted
  `;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-4">
        <Input
          type="number"
          onChange={onRangeInputChange("min")}
          value={min ?? ""}
          placeholder={moreThenPlaceholder}
          className={className}
        />
        <Input
          type="number"
          onChange={onRangeInputChange("max")}
          value={max ?? ""}
          placeholder={lessThenPlaceholder}
          className={className}
        />
      </div>
      {error && (
        <p className={cn("text-[0.8rem] font-medium text-destructive")}>
          {error}
        </p>
      )}
    </div>
  );
};

export const FilterNumeric = <T extends string>({
  label,
  name,
  icon,
  placeholders = ["More than", "Less than"],
  defaultValue = {},
  findFilterByName,
  updateFilter,
  removeFilter,
  isFilterActive,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  placeholders?: string[];
  icon: ReactNode;
  defaultValue?: FilterNumericValue;
} & FilterMethods<T>) => {
  const [moreThanPlaceholder, lessThanPlaceholder] = placeholders;

  const value = useMemo(
    () => (findFilterByName(name)?.value as FilterNumericValue) || defaultValue,
    [defaultValue, findFilterByName, name],
  );

  const onRangeInputChange =
    (key: "min" | "max") => (e: ChangeEvent<HTMLInputElement>) => {
      updateFilter({
        name,
        type: FilterType.numeric,
        value: {
          ...value,
          [key]: e.target.value,
        } as FilterNumericValue,
      });
    };

  const onResetClick = useCallback(() => {
    removeFilter(name);
  }, [name, removeFilter]);

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <FilterNumericRange
          {...{
            min: value.min,
            max: value.max,
            moreThenPlaceholder: moreThanPlaceholder,
            lessThenPlaceholder: lessThanPlaceholder,
            onRangeInputChange,
          }}
        />
        <Button
          size="sm"
          variant="link"
          onClick={onResetClick}
          className="mt-4 flex h-auto items-center gap-2 p-0 text-muted"
        >
          <LuRefreshCcw />
          Reset
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterBoolean = <T extends string>({
  label,
  name,
  icon,
  placeholder,
  defaultValue = false,
  findFilterByName,
  updateFilter,
  removeFilter,
  isFilterActive,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  placeholder?: string;
  icon: ReactNode;
  defaultValue?: boolean;
} & FilterMethods<T>) => {
  const checked = findFilterByName(name)?.value || defaultValue;

  const onCheckedChange = useCallback(
    (enabled: boolean) => {
      updateFilter({
        name,
        type: FilterType.boolean,
        value: enabled,
      });
    },
    [name, updateFilter],
  );

  const onResetClick = useCallback(() => {
    removeFilter(name);
  }, [name, removeFilter]);

  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div
          className="
            flex w-full items-center justify-between rounded-3xl border p-2 px-4
            text-sm
          "
        >
          <div>
            <span className="font-semibold text-muted-foreground">
              {placeholder}
            </span>
          </div>
          <div>
            <Switch
              {...{ checked: checked as boolean, onCheckedChange, id: label }}
            />
          </div>
        </div>
        <Button
          size="sm"
          variant="link"
          onClick={onResetClick}
          className="mt-4 flex h-auto items-center gap-2 p-0 text-muted"
        >
          <LuRefreshCcw />
          Reset
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FilterMultiUser = <T extends string>({
  label,
  name,
  icon,
  placeholder,
  buttonClassName = "",
  defaultUsers,
  updateFilter,
  isFilterActive,
  pillsContainerRightPadding,
  isFilterDisabled,
}: {
  label: string;
  name: T;
  placeholder?: string;
  icon: ReactNode;
  value?: PartialAccount[];
  buttonClassName?: string;
  defaultUsers?: PartialAccount[];
  pillsContainerRightPadding?: number;
} & FilterMethods<T>) => {
  if (isFilterDisabled?.(name)) return null;

  return (
    <AccordionItem value={name}>
      <AccordionTrigger className="m-0 py-2">
        <FilterWrapper<T> {...{ label, name, icon, isFilterActive }} />
      </AccordionTrigger>
      <AccordionContent className="mb-4 overflow-visible rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <UserMultipleSelect
            pillsContainerRightPadding={pillsContainerRightPadding}
            placeholder={placeholder}
            buttonClassName={buttonClassName}
            users={defaultUsers}
            onSelectedUsers={(users) => {
              updateFilter({
                name,
                type: FilterType.multiUserSelect,
                value: users,
              });
            }}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
